/**
 * Mock data for the Scan Analytics prototype dashboard.
 * Based on the 20-sample parallel-run test from Notion:
 * "Extraction Accuracy for 20 sample files — 1st day of Parallel Run"
 * (16 proxy scans S-P6–S-P21, 4 corp action scans S-R1–S-R4)
 *
 * Issuer scans (S-I*) share the general job tag with Proxy/CA. Regulatory
 * scans use the Regulatory tag's date-based SCAN# (MMDDYY + sequence) and
 * its own field set (see REG_FIELDS).
 *
 * All current scans are Week 1 (July 1, 2026 — first day of parallel run).
 * Extend MOCK_SCANS with week: 2, 3… as real weeks accumulate.
 */

export interface ScanDetail {
  scan_id: string;
  client_name: string;
  job_category: 'P' | 'CA' | 'R' | 'I';
  week: number;     // 1-indexed parallel run week
  date: string;     // ISO date "2026-07-01"
  fields_correct: number;
  fields_total: number;
  accuracy_pct: number;
  issues: string[];
}

export const MOCK_SCANS: ScanDetail[] = [
  // ── Proxy scans — Week 1 (Jul 1 2026) ────────────────────────────────────
  {
    scan_id: 'S-P6',  client_name: 'Aramark',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 17, fields_total: 17, accuracy_pct: 100, issues: [],
  },
  {
    scan_id: 'S-P7',  client_name: 'Huachen AI Parking Mgmt Tech Hldg',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 14, fields_total: 16, accuracy_pct: 87.5, issues: ['Event Date'],
  },
  {
    scan_id: 'S-P8',  client_name: 'Top Wealth Group Holding Ltd',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 15, fields_total: 16, accuracy_pct: 93.8, issues: ['Event Date'],
  },
  {
    scan_id: 'S-P9',  client_name: 'TJGC Group Ltd',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 15, fields_total: 16, accuracy_pct: 93.8, issues: ['Record Date'],
  },
  {
    scan_id: 'S-P10', client_name: 'Ballston Spa Bancorp',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 16, fields_total: 16, accuracy_pct: 100, issues: [],
  },
  {
    scan_id: 'S-P11', client_name: 'Phas Tech Holding Ltd',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 16, fields_total: 17, accuracy_pct: 94.1, issues: ['Documents Received'],
  },
  {
    scan_id: 'S-P12', client_name: 'Chaince Digital Holding Inc',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 13, fields_total: 16, accuracy_pct: 81.3,
    issues: ['Event Date', 'Sample Weight', 'Documents Received'],
  },
  {
    scan_id: 'S-P13', client_name: 'Maris-Tech Ltd',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 16, fields_total: 16, accuracy_pct: 100, issues: [],
  },
  {
    scan_id: 'S-P14', client_name: 'Micron Solution Inc',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 12, fields_total: 16, accuracy_pct: 75.0,
    issues: ['Event Date', 'Record Date', 'Carrier', 'Documents Received'],
  },
  {
    scan_id: 'S-P15', client_name: 'Horizon Group Properties Inc',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 15, fields_total: 16, accuracy_pct: 93.8, issues: ['Documents Received'],
  },
  {
    scan_id: 'S-P16', client_name: 'Alithya Group Inc',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 14, fields_total: 16, accuracy_pct: 87.5,
    issues: ['Record Date', 'Documents Received'],
  },
  {
    scan_id: 'S-P17', client_name: 'Rapid Dos Therapeutics Corp',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 19, fields_total: 20, accuracy_pct: 95.0, issues: ['Documents Received'],
  },
  {
    scan_id: 'S-P18', client_name: 'Allspring Multi-Sector Income Fund',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 18, fields_total: 20, accuracy_pct: 90.0,
    issues: ['Documents Received', 'Sample Weight'],
  },
  {
    scan_id: 'S-P19', client_name: 'Black Box Stocks Inc',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 16, fields_total: 16, accuracy_pct: 100, issues: [],
  },
  {
    scan_id: 'S-P20', client_name: 'Biomind Labs Inc',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 14, fields_total: 16, accuracy_pct: 87.5,
    issues: ['Carrier', 'Documents Received'],
  },
  {
    scan_id: 'S-P21', client_name: 'Calian Group Ltd',
    job_category: 'P', week: 1, date: '2026-07-01',
    fields_correct: 17, fields_total: 17, accuracy_pct: 100, issues: [],
  },

  // ── Corp Action scans — Week 1 (Jul 1 2026) ──────────────────────────────
  {
    scan_id: 'S-R1', client_name: 'Office Properties Income Trust',
    job_category: 'CA', week: 1, date: '2026-07-01',
    fields_correct: 15, fields_total: 15, accuracy_pct: 100, issues: [],
  },
  {
    scan_id: 'S-R2', client_name: 'NXG Next Gen Infrastructure Income Fund',
    job_category: 'CA', week: 1, date: '2026-07-01',
    fields_correct: 16, fields_total: 17, accuracy_pct: 94.1, issues: ['Documents Received'],
  },
  {
    scan_id: 'S-R3', client_name: 'Office Properties Income Trust',
    job_category: 'CA', week: 1, date: '2026-07-01',
    fields_correct: 13, fields_total: 15, accuracy_pct: 86.7,
    issues: ['Location', 'Carrier'],
  },
  {
    scan_id: 'S-R4', client_name: 'The Gabelli Equity Trust Inc',
    job_category: 'CA', week: 1, date: '2026-07-01',
    fields_correct: 10, fields_total: 15, accuracy_pct: 66.7,
    issues: ['Location', 'Event Date', 'Record Date', 'Carrier', 'Documents Received'],
  },

  // ── Proxy scans — Week 2 (Jul 8 2026) ────────────────────────────────────
  { scan_id: 'S-P22', client_name: 'Aramark',                   job_category: 'P', week: 2, date: '2026-07-08', fields_correct: 17, fields_total: 17, accuracy_pct: 100,  issues: [] },
  { scan_id: 'S-P23', client_name: 'BKV Corp',                  job_category: 'P', week: 2, date: '2026-07-08', fields_correct: 16, fields_total: 16, accuracy_pct: 100,  issues: [] },
  { scan_id: 'S-P24', client_name: 'Calian Group Ltd',          job_category: 'P', week: 2, date: '2026-07-08', fields_correct: 16, fields_total: 17, accuracy_pct: 94.1, issues: ['Documents Received'] },
  { scan_id: 'S-P25', client_name: 'Enlivex Therapeutics Ltd',  job_category: 'P', week: 2, date: '2026-07-08', fields_correct: 15, fields_total: 16, accuracy_pct: 93.8, issues: ['Record Date'] },
  { scan_id: 'S-P26', client_name: 'Invo Fertility Inc',        job_category: 'P', week: 2, date: '2026-07-08', fields_correct: 16, fields_total: 16, accuracy_pct: 100,  issues: [] },
  { scan_id: 'S-P27', client_name: 'Black Box Stocks Inc',      job_category: 'P', week: 2, date: '2026-07-08', fields_correct: 15, fields_total: 16, accuracy_pct: 93.8, issues: ['Event Date'] },
  { scan_id: 'S-P28', client_name: 'Biomind Labs Inc',          job_category: 'P', week: 2, date: '2026-07-08', fields_correct: 14, fields_total: 16, accuracy_pct: 87.5, issues: ['Carrier', 'Documents Received'] },
  { scan_id: 'S-P29', client_name: 'Good Times Restaurant Inc', job_category: 'P', week: 2, date: '2026-07-08', fields_correct: 15, fields_total: 16, accuracy_pct: 93.8, issues: ['Sample Weight'] },
  { scan_id: 'S-P30', client_name: 'Pasithea Therapeutics Corp',job_category: 'P', week: 2, date: '2026-07-08', fields_correct: 16, fields_total: 16, accuracy_pct: 100,  issues: [] },

  // ── Corp Action scans — Week 2 (Jul 8 2026) ──────────────────────────────
  { scan_id: 'S-R5', client_name: 'Office Properties Income Trust',          job_category: 'CA', week: 2, date: '2026-07-08', fields_correct: 15, fields_total: 15, accuracy_pct: 100,  issues: [] },
  { scan_id: 'S-R6', client_name: 'NXG Next Gen Infrastructure Income Fund', job_category: 'CA', week: 2, date: '2026-07-08', fields_correct: 16, fields_total: 17, accuracy_pct: 94.1, issues: ['Documents Received'] },
  { scan_id: 'S-R7', client_name: 'The Gabelli Equity Trust Inc',            job_category: 'CA', week: 2, date: '2026-07-08', fields_correct: 13, fields_total: 15, accuracy_pct: 86.7, issues: ['Location', 'Carrier'] },

  // ── Proxy scans — Week 3 (Jul 15 2026) ───────────────────────────────────
  { scan_id: 'S-P31', client_name: 'Aramark',                             job_category: 'P', week: 3, date: '2026-07-15', fields_correct: 17, fields_total: 17, accuracy_pct: 100,  issues: [] },
  { scan_id: 'S-P32', client_name: 'BKV Corp',                            job_category: 'P', week: 3, date: '2026-07-15', fields_correct: 16, fields_total: 16, accuracy_pct: 100,  issues: [] },
  { scan_id: 'S-P33', client_name: 'Calian Group Ltd',                    job_category: 'P', week: 3, date: '2026-07-15', fields_correct: 17, fields_total: 17, accuracy_pct: 100,  issues: [] },
  { scan_id: 'S-P34', client_name: 'Fitness Champs Holding Ltd',          job_category: 'P', week: 3, date: '2026-07-15', fields_correct: 15, fields_total: 16, accuracy_pct: 93.8, issues: ['Documents Received'] },
  { scan_id: 'S-P35', client_name: 'Allspring Global Dividend Opp Fund',  job_category: 'P', week: 3, date: '2026-07-15', fields_correct: 19, fields_total: 20, accuracy_pct: 95.0, issues: ['Record Date'] },
  { scan_id: 'S-P36', client_name: 'Invo Fertility Inc',                  job_category: 'P', week: 3, date: '2026-07-15', fields_correct: 16, fields_total: 16, accuracy_pct: 100,  issues: [] },
  { scan_id: 'S-P37', client_name: 'Black Box Stocks Inc',                job_category: 'P', week: 3, date: '2026-07-15', fields_correct: 16, fields_total: 16, accuracy_pct: 100,  issues: [] },
  { scan_id: 'S-P38', client_name: 'Aeon Biopharma Inc',                  job_category: 'P', week: 3, date: '2026-07-15', fields_correct: 14, fields_total: 16, accuracy_pct: 87.5, issues: ['Event Date', 'Carrier'] },

  // ── Corp Action scans — Week 3 (Jul 15 2026) ─────────────────────────────
  { scan_id: 'S-R8',  client_name: 'Office Properties Income Trust',          job_category: 'CA', week: 3, date: '2026-07-15', fields_correct: 15, fields_total: 15, accuracy_pct: 100,  issues: [] },
  { scan_id: 'S-R9',  client_name: 'NXG Next Gen Infrastructure Income Fund', job_category: 'CA', week: 3, date: '2026-07-15', fields_correct: 17, fields_total: 17, accuracy_pct: 100,  issues: [] },
  { scan_id: 'S-R10', client_name: 'The Gabelli Equity Trust Inc',            job_category: 'CA', week: 3, date: '2026-07-15', fields_correct: 14, fields_total: 15, accuracy_pct: 93.3, issues: ['Location'] },

  // ── Issuer scans — same general tag as Proxy ─────────────────────────────
  { scan_id: 'S-I1', client_name: 'Sable Point Resources',     job_category: 'I', week: 1, date: '2026-07-01', fields_correct: 15, fields_total: 16, accuracy_pct: 93.8, issues: ['Distributor'] },
  { scan_id: 'S-I2', client_name: 'Brookfield Paper Co',       job_category: 'I', week: 1, date: '2026-07-01', fields_correct: 16, fields_total: 16, accuracy_pct: 100,  issues: [] },
  { scan_id: 'S-I3', client_name: 'Thunder Bay Minerals Corp', job_category: 'I', week: 2, date: '2026-07-08', fields_correct: 13, fields_total: 16, accuracy_pct: 81.3, issues: ['Event Date', 'Record Date', 'Documents Received'] },
  { scan_id: 'S-I4', client_name: 'Sable Point Resources',     job_category: 'I', week: 2, date: '2026-07-08', fields_correct: 16, fields_total: 16, accuracy_pct: 100,  issues: [] },
  { scan_id: 'S-I5', client_name: 'Hemlock Point Resorts',     job_category: 'I', week: 3, date: '2026-07-15', fields_correct: 15, fields_total: 16, accuracy_pct: 93.8, issues: ['Documents Received'] },
  { scan_id: 'S-I6', client_name: 'Brookfield Paper Co',       job_category: 'I', week: 3, date: '2026-07-15', fields_correct: 16, fields_total: 16, accuracy_pct: 100,  issues: [] },

  // ── Regulatory scans — Regulatory tag, date-based SCAN# ──────────────────
  { scan_id: '07012601', client_name: 'JPMorgan',       job_category: 'R', week: 1, date: '2026-07-01', fields_correct: 11, fields_total: 11, accuracy_pct: 100,  issues: [] },
  { scan_id: '07012602', client_name: 'First Trust',    job_category: 'R', week: 1, date: '2026-07-01', fields_correct: 10, fields_total: 11, accuracy_pct: 90.9, issues: ['Tracking #'] },
  { scan_id: '07012603', client_name: 'Kayne Anderson', job_category: 'R', week: 1, date: '2026-07-01', fields_correct: 8,  fields_total: 11, accuracy_pct: 72.7, issues: ['Addressed To', 'Tracking #', 'Quantity'] },
  { scan_id: '07082601', client_name: 'JPMorgan',       job_category: 'R', week: 2, date: '2026-07-08', fields_correct: 10, fields_total: 11, accuracy_pct: 90.9, issues: ['Quantity'] },
  { scan_id: '07082602', client_name: 'First Trust',    job_category: 'R', week: 2, date: '2026-07-08', fields_correct: 11, fields_total: 11, accuracy_pct: 100,  issues: [] },
  { scan_id: '07152601', client_name: 'Kayne Anderson', job_category: 'R', week: 3, date: '2026-07-15', fields_correct: 10, fields_total: 11, accuracy_pct: 90.9, issues: ['Addressed To'] },
  { scan_id: '07152602', client_name: 'JPMorgan',       job_category: 'R', week: 3, date: '2026-07-15', fields_correct: 11, fields_total: 11, accuracy_pct: 100,  issues: [] },
];

// ── Helpers — all accept an optional pre-filtered scans array ──────────────

export function mockScansForWeek(week: number | null): ScanDetail[] {
  return week === null ? MOCK_SCANS : MOCK_SCANS.filter(s => s.week === week);
}

export function availableMockWeeks(): number[] {
  return [...new Set(MOCK_SCANS.map(s => s.week))].sort((a, b) => a - b);
}

export function mockSummary(scans: ScanDetail[] = MOCK_SCANS) {
  const total = scans.length;
  if (total === 0) return { total_receipts: 0, overall_avg_score_pct: 0 };
  const avgPct = Math.round(scans.reduce((s, r) => s + r.accuracy_pct, 0) / total * 10) / 10;
  return { total_receipts: total, overall_avg_score_pct: avgPct };
}

export function mockScanTypeBreakdown(scans: ScanDetail[] = MOCK_SCANS) {
  const of = (cat: ScanDetail['job_category']) => scans.filter(s => s.job_category === cat);
  const stats = (arr: ScanDetail[]) => ({
    total:         arr.length,
    avg_score_pct: arr.length
      ? Math.round(arr.reduce((s, r) => s + r.accuracy_pct, 0) / arr.length * 10) / 10
      : 0,
    high: arr.filter(s => s.accuracy_pct === 100).length,
    mid:  arr.filter(s => s.accuracy_pct >= 80 && s.accuracy_pct < 100).length,
    low:  arr.filter(s => s.accuracy_pct < 80).length,
  });
  return { proxy: stats(of('P')), corp_action: stats(of('CA')), regulatory: stats(of('R')), issuer: stats(of('I')) };
}

// General job tag (Proxy, Corp Action, Issuer).
const ALL_FIELDS = [
  'Scan Number', 'Client', 'Event Date', 'Record Date',
  'Location', 'Distributor', 'Carrier', 'Packing Slip',
  'Envelopes', 'Packages', 'Boxes', 'Skids', 'Mail Envelope Size', 'Sample Weight',
  'Documents Received', 'Job Category',
];

// Regulatory job tag.
const REG_FIELDS = [
  'Scan Number', 'Client', 'Location', 'Distributor', 'Carrier', 'Packing Slip',
  'Boxes', 'Tracking #', 'Addressed To', 'Quantity', 'Job Category',
];

const fieldsFor = (cat: ScanDetail['job_category']) => (cat === 'R' ? REG_FIELDS : ALL_FIELDS);

export function mockFieldAccuracy(scans: ScanDetail[] = MOCK_SCANS) {
  return _fieldAccuracyFrom(scans);
}

export function mockFieldAccuracyByCategory(category: ScanDetail['job_category'], scans: ScanDetail[] = MOCK_SCANS) {
  return _fieldAccuracyFrom(scans.filter(s => s.job_category === category));
}

// Each field is scored only over the scans whose tag carries it.
function _fieldAccuracyFrom(scans: ScanDetail[]) {
  if (scans.length === 0) return [];
  const totals: Record<string, number> = {};
  const wrongCounts: Record<string, number> = {};
  for (const scan of scans) {
    for (const f of fieldsFor(scan.job_category)) totals[f] = (totals[f] ?? 0) + 1;
    for (const f of scan.issues) wrongCounts[f] = (wrongCounts[f] ?? 0) + 1;
  }
  return Object.entries(totals).map(([field, n]) => ({
    field,
    correct_pct: Math.round(((n - (wrongCounts[field] ?? 0)) / n) * 1000) / 10,
    total: n,
  })).sort((a, b) => a.correct_pct - b.correct_pct);
}

/** Weekly trend data derived from MOCK_SCANS (all weeks). */
export function mockWeeklyTrend() {
  const byWeek: Record<number, ScanDetail[]> = {};
  for (const s of MOCK_SCANS) {
    (byWeek[s.week] ??= []).push(s);
  }
  return Object.entries(byWeek).map(([w, scans]) => {
    const sides = Object.values(mockScanTypeBreakdown(scans));
    return {
      date:        `Week ${w}`,
      Volume:      scans.length,
      'Accuracy %': mockSummary(scans).overall_avg_score_pct,
      High:        sides.reduce((n, s) => n + s.high, 0),
      Mid:         sides.reduce((n, s) => n + s.mid, 0),
      Low:         sides.reduce((n, s) => n + s.low, 0),
    };
  });
}
